/**
 * Wiring: fetch the shared state, tick the clock against the server's idea of
 * now, and hand the buttons to `controls.ts`.
 *
 * ---------------------------------------------------------------------------
 * WHY THE TICK IS NOT `setInterval(1000)`
 * ---------------------------------------------------------------------------
 * A one-second interval is not aligned to anything. It fires 1000 ms after the
 * page happened to load, so the seconds digit changes at some arbitrary offset
 * within each real second - and two devices opened a few hundred milliseconds
 * apart visibly tick out of step even though they agree exactly on the state.
 * On top of that `setInterval` drifts, and a throttled background tab drops
 * ticks it never makes up.
 *
 * Each tick therefore schedules the next one for the moment the display will
 * actually change, which is the fractional part of the time remaining. Devices
 * converge on the same instant because they are all aiming at the same target.
 */

import './styles.css'

import { serverNow } from './clock.ts'
import { mountControls } from './controls.ts'
import { mountDisplay } from './display.ts'
import { isEndless } from './shared/countdown.ts'
import { createSync, type Sync } from './sync.ts'

const clockEl = must<HTMLElement>('#clock')
const statusEl = must<HTMLElement>('#status')
const announcerEl = must<HTMLElement>('#announcer')
const controlsEl = must<HTMLElement>('#controls')

const display = mountDisplay(clockEl, announcerEl)
const sync = createSync()

const controls = mountControls(controlsEl, {
  onPress: (mode) => void sync.press(mode),
  announce: (message) => {
    announcerEl.textContent = message
  }
})

let latest: Sync = sync.current()
let lastChangedAt = latest.state.changedAt
let firstLoad = true
let tickTimer: number | undefined

sync.subscribe((next) => {
  const changedElsewhere = !firstLoad && next.state.changedAt !== lastChangedAt
  lastChangedAt = next.state.changedAt
  latest = next
  if (changedElsewhere) {
    // Someone pressed a button on another device. Disarm anything primed here
    // first: confirming a press against state that has already moved is how you
    // end up wiping out a change you never saw.
    controls.reset()
  }
  firstLoad = false
  paint()
})

function paint(): void {
  display.render(serverNow(), latest.state)
  statusEl.textContent = statusLine(latest)
  statusEl.dataset.link = latest.link

  window.clearTimeout(tickTimer)
  if (document.visibilityState === 'hidden') return
  // No per-second wake-ups for a static symbol. Sync still polls, and receiving
  // a finite mode through the subscription starts the tick again.
  if (isEndless(latest.state)) return
  // The fractional millisecond remainder is exactly how long until the seconds
  // digit changes. `|| 1000` covers the once-in-a-thousand landing dead on a
  // boundary, where a 0 ms timeout would spin.
  const untilChange = latest.state.target - serverNow()
  const delay = untilChange > 0 ? (untilChange % 1000 || 1000) : 1000
  tickTimer = window.setTimeout(paint, Math.max(16, delay))
}

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') paint()
})

/**
 * The status line, which is empty whenever there is nothing wrong.
 *
 * It used to narrate the healthy case - "shared with all your devices, in step
 * with the server's clock, last changed just now" - and that was a permanent
 * line of small print under a page whose entire content is one enormous number.
 * Sync working is the expected case and does not need announcing; a screenful
 * of reassurance is just clutter, and clutter next to the thing you are meant
 * to be looking at is worse than clutter anywhere else.
 *
 * The failure cases DO need announcing, because the digits look identical
 * whether they are live or forty minutes stale. So this returns text only when
 * something is actually wrong, and `styles.css` collapses the element when it
 * is empty so the layout does not reserve a gap for it.
 */
function statusLine(state: Sync): string {
  if (state.link === 'offline') {
    return `Offline — showing the last countdown this device saw.${state.problem ? ` ${state.problem}` : ''}`
  }
  if (state.link === 'unshared') {
    return 'Not shared yet — no database is connected, so presses will not reach your other devices.'
  }
  return ''
}

function must<T extends Element>(selector: string): T {
  const found = document.querySelector<T>(selector)
  if (!found) throw new Error(`The markup is missing ${selector}.`)
  return found
}

paint()
sync.start()
